import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ConsentDetails, formatConsentId, formatRelativeTime, isExpiringSoon, DataCluster } from '../../constants/uaeStandards';
import { ConsentStateBadge } from '../ui/ConsentStateBadge';
import { Button } from '../ui/Button';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
import { ArrowLeftIcon, CalendarIcon, ActivityIcon, CopyIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, AlertTriangleIcon, PauseIcon, PlayIcon, XCircleIcon, EditIcon, ReceiptIcon, UserIcon, DollarSignIcon, CreditCardIcon, InfoIcon, UsersIcon, FileTextIcon } from 'lucide-react';
interface ConsentDetailedViewProps {
  consent: ConsentDetails;
  onBack: () => void;
  onRevoke: () => void;
  onPause?: () => void;
  onReactivate?: () => void;
  onUpdate?: () => void;
  context: 'TPP' | 'LFI';
}
// Icon mapping for data clusters - VeloX branded
const dataClusterIconMap: Record<string, ComponentType<any>> = {
  User: UserIcon,
  DollarSign: DollarSignIcon,
  CreditCard: CreditCardIcon,
  Info: InfoIcon,
  Users: UsersIcon,
  FileText: FileTextIcon
};
// Helper to get icon component for a data cluster
function getIconComponent(iconName?: string): ComponentType<any> {
  if (!iconName) return FileTextIcon;
  return dataClusterIconMap[iconName] || FileTextIcon;
}
export function ConsentDetailedView({
  consent,
  onBack,
  onRevoke,
  onPause,
  onReactivate,
  onUpdate,
  context
}: ConsentDetailedViewProps) {
  const [expandedClusters, setExpandedClusters] = useState<string[]>([]);
  const [copiedId, setCopiedId] = useState(false);
  const isTPP = context === 'TPP';
  const showExpiryWarning = isExpiringSoon(consent.expiryDate);
  const canPause = consent.state === 'Authorized' && isTPP;
  const canReactivate = consent.state === 'Paused' && isTPP;
  const canRevoke = ['Authorized', 'Paused', 'Suspended'].includes(consent.state);
  const canUpdate = consent.state === 'Authorized' && isTPP && onUpdate;
  const toggleCluster = (cluster: string) => {
    setExpandedClusters(prev => prev.includes(cluster) ? prev.filter(c => c !== cluster) : [...prev, cluster]);
  };
  const copyConsentId = () => {
    navigator.clipboard.writeText(consent.consentId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };
  return <div className="space-y-6" data-id="element-397">
      {/* Header - Removed back button since it's now in CMITopBar */}
      <div data-id="element-398">
        <div className="flex items-start justify-between" data-id="element-399">
          <div className="flex items-center gap-4" data-id="element-400">
            <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center p-3 bg-gray-50" style={{
            borderRadius: OPENFINANCE_THEME.radius.lg
          }} data-id="element-401">
              <img src={isTPP ? consent.bankLogo : consent.tppLogo} alt={isTPP ? consent.bankName : consent.tppName} className="w-full h-full object-contain" data-id="element-402" />
            </div>
            <div data-id="element-403">
              <h1 className="text-2xl font-bold mb-1" style={{
              fontFamily: OPENFINANCE_THEME.fonts.display,
              color: OPENFINANCE_THEME.colors.primary700
            }} data-id="element-404">
                {isTPP ? consent.bankName : consent.tppTradingName}
              </h1>
              <div className="text-sm" style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-405">
                {consent.merchantName && `${consent.merchantName} via `}
                {consent.type === 'DataSharing' ? 'Data Sharing' : 'Service Initiation'}
              </div>
            </div>
          </div>
          <ConsentStateBadge state={consent.state} size="lg" data-id="element-406" />
        </div>
      </div>

      {/* Expiry Warning */}
      {showExpiryWarning && consent.state === 'Authorized' && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex items-start gap-3 p-4 rounded-xl" style={{
      backgroundColor: OPENFINANCE_THEME.colors.accent300 + '20',
      border: `2px solid ${OPENFINANCE_THEME.colors.accent500}`
    }} data-id="element-407">
          <AlertTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
        color: OPENFINANCE_THEME.colors.accent600
      }} data-id="element-408" />
          <div data-id="element-409">
            <div className="font-semibold text-sm mb-1" style={{
          color: OPENFINANCE_THEME.colors.accent600
        }} data-id="element-410">
              Consent Expiring Soon
            </div>
            <div className="text-xs" style={{
          color: OPENFINANCE_THEME.colors.accent600
        }} data-id="element-411">
              This consent will expire on {consent.expiryDate}. Consider
              renewing to maintain access.
            </div>
          </div>
        </motion.div>}

      {/* Consent Details Card */}
      <div className="p-6 border-2" style={{
      backgroundColor: OPENFINANCE_THEME.colors.cardBg,
      borderColor: OPENFINANCE_THEME.colors.border,
      borderRadius: OPENFINANCE_THEME.radius.lg
    }} data-id="element-412">
        <div className="text-sm font-semibold uppercase tracking-wide mb-4" style={{
        color: OPENFINANCE_THEME.colors.primary500
      }} data-id="element-413">
          Consent Information
        </div>

        <div className="space-y-4" data-id="element-414">
          {/* Consent ID */}
          <div className="flex items-center justify-between" data-id="element-415">
            <div data-id="element-416">
              <div className="text-xs font-medium mb-1" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-417">
                Consent ID
              </div>
              <div className="font-mono text-sm font-medium" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-418">
                {formatConsentId(consent.consentId)}
              </div>
            </div>
            <button onClick={copyConsentId} className="p-2 transition-colors rounded-lg hover:bg-gray-100" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-419">
              {copiedId ? <CheckIcon className="w-5 h-5" data-id="element-420" /> : <CopyIcon className="w-5 h-5" data-id="element-421" />}
            </button>
          </div>

          {/* Account Details */}
          <div data-id="element-422">
            <div className="text-xs font-medium mb-1" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-423">
              Connected Account
            </div>
            <div className="text-sm font-semibold" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-424">
              {consent.accountType}
            </div>
            <div className="text-xs font-mono mt-0.5" style={{
            color: OPENFINANCE_THEME.colors.textLight
          }} data-id="element-425">
              ****{consent.accountLastFour}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4" data-id="element-426">
            <div data-id="element-427">
              <div className="text-xs font-medium mb-1" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-428">
                Connected Date
              </div>
              <div className="flex items-center gap-2" data-id="element-429">
                <CalendarIcon className="w-4 h-4" style={{
                color: OPENFINANCE_THEME.colors.textLight
              }} data-id="element-430" />
                <span className="text-sm font-medium" style={{
                color: OPENFINANCE_THEME.colors.text
              }} data-id="element-431">
                  {consent.connectedDate}
                </span>
              </div>
            </div>

            <div data-id="element-432">
              <div className="text-xs font-medium mb-1" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-433">
                Expiry Date
              </div>
              <div className="flex items-center gap-2" data-id="element-434">
                <CalendarIcon className="w-4 h-4" style={{
                color: OPENFINANCE_THEME.colors.textLight
              }} data-id="element-435" />
                <span className="text-sm font-medium" style={{
                color: OPENFINANCE_THEME.colors.text
              }} data-id="element-436">
                  {consent.expiryDate}
                </span>
              </div>
            </div>
          </div>

          {/* Last Access */}
          {consent.lastAccessDate && <div data-id="element-437">
              <div className="text-xs font-medium mb-1" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-438">
                Last Access
              </div>
              <div className="flex items-center gap-2" data-id="element-439">
                <ActivityIcon className="w-4 h-4" style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-440" />
                <span className="text-sm font-medium" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-441">
                  {formatRelativeTime(consent.lastAccessDate)}
                </span>
              </div>
            </div>}
        </div>
      </div>

      {/* Data Clusters with VeloX branded icons */}
      <div data-id="element-442">
        <div className="text-sm font-semibold uppercase tracking-wide mb-3" style={{
        color: OPENFINANCE_THEME.colors.text
      }} data-id="element-443">
          Data You're Sharing
        </div>

        <div className="space-y-3" data-id="element-444">
          {consent.dataClusters.map(cluster => {
          const isExpanded = expandedClusters.includes(cluster.cluster);
          const IconComponent = getIconComponent(cluster.icon);
          return <div key={cluster.cluster} className="border-2 overflow-hidden" style={{
            borderColor: OPENFINANCE_THEME.colors.border,
            borderRadius: OPENFINANCE_THEME.radius.lg,
            backgroundColor: OPENFINANCE_THEME.colors.cardBg
          }} data-id="element-445">
                <button onClick={() => toggleCluster(cluster.cluster)} className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-446">
                  <div className="flex items-center gap-3 text-left" data-id="element-447">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: OPENFINANCE_THEME.colors.primary50,
                  borderRadius: OPENFINANCE_THEME.radius.md
                }} data-id="element-448">
                      <IconComponent className="w-5 h-5" style={{
                    color: OPENFINANCE_THEME.colors.primary500
                  }} data-id="element-449" />
                    </div>
                    <div data-id="element-450">
                      <div className="font-semibold text-sm" style={{
                    color: OPENFINANCE_THEME.colors.text
                  }} data-id="element-451">
                        {cluster.cluster}
                      </div>
                      <div className="text-xs mt-0.5" style={{
                    color: OPENFINANCE_THEME.colors.textMuted
                  }} data-id="element-452">
                        {cluster.permissions.length} permission
                        {cluster.permissions.length > 1 ? 's' : ''}
                        {cluster.lastAccessed && ` • Last accessed ${formatRelativeTime(cluster.lastAccessed)}`}
                      </div>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: isExpanded ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-453">
                    <ChevronDownIcon className="w-5 h-5" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-454" />
                  </motion.div>
                </button>

                <AnimatePresence data-id="element-455">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-456">
                      <div className="border-t-2 p-4 space-y-3" style={{
                  borderColor: OPENFINANCE_THEME.colors.borderLight,
                  backgroundColor: OPENFINANCE_THEME.colors.primary50
                }} data-id="element-457">
                        {cluster.permissions.map(permission => <div key={permission.id} data-id="element-458">
                            <div className="text-sm font-medium mb-1" style={{
                      color: OPENFINANCE_THEME.colors.text
                    }} data-id="element-459">
                              {permission.language}
                            </div>
                            <div className="text-xs" style={{
                      color: OPENFINANCE_THEME.colors.textLight
                    }} data-id="element-460">
                              Examples: {permission.examples}
                            </div>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* Transaction Log (Service Initiation only) */}
      {consent.type === 'ServiceInitiation' && consent.transactionLog && consent.transactionLog.length > 0 && <div data-id="element-461">
            <div className="text-sm font-semibold uppercase tracking-wide mb-3 flex items-center gap-2" style={{
        color: OPENFINANCE_THEME.colors.text
      }} data-id="element-462">
              <ReceiptIcon className="w-4 h-4" data-id="element-463" />
              <span data-id="element-464">Transaction Log</span>
            </div>

            <div className="border-2 overflow-hidden" style={{
        borderColor: OPENFINANCE_THEME.colors.border,
        borderRadius: OPENFINANCE_THEME.radius.lg
      }} data-id="element-465">
              {consent.transactionLog.map((transaction, index) => <div key={transaction.id} className="p-4 flex items-center justify-between" style={{
          borderBottom: index < consent.transactionLog!.length - 1 ? `1px solid ${OPENFINANCE_THEME.colors.borderLight}` : 'none'
        }} data-id="element-466">
                  <div data-id="element-467">
                    <div className="font-medium text-sm" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-468">
                      {transaction.description}
                    </div>
                    <div className="text-xs mt-0.5" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-469">
                      {transaction.date}
                    </div>
                  </div>
                  <div className="text-right" data-id="element-470">
                    {transaction.amount && <div className="font-semibold text-sm" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-471">
                        {transaction.currency} {transaction.amount.toFixed(2)}
                      </div>}
                    <div className="text-xs mt-0.5" style={{
              color: transaction.status === 'Completed' ? '#2E7D32' : transaction.status === 'Failed' ? '#C62828' : '#E65100'
            }} data-id="element-472">
                      {transaction.status}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}

      {/* Actions */}
      <div className="space-y-3 pt-4 border-t-2" style={{
      borderColor: OPENFINANCE_THEME.colors.borderLight
    }} data-id="element-473">
        {canReactivate && onReactivate && <Button onClick={onReactivate} variant="accent" className="w-full" data-id="element-474">
            <div className="flex items-center justify-center gap-2" data-id="element-475">
              <PlayIcon className="w-5 h-5" data-id="element-476" />
              <span data-id="element-477">Reactivate Consent</span>
            </div>
          </Button>}

        {canPause && onPause && <Button onClick={onPause} variant="secondary" className="w-full" data-id="element-478">
            <div className="flex items-center justify-center gap-2" data-id="element-479">
              <PauseIcon className="w-5 h-5" data-id="element-480" />
              <span data-id="element-481">Pause Consent</span>
            </div>
          </Button>}

        {canUpdate && <Button onClick={onUpdate} variant="secondary" className="w-full" data-id="element-482">
            <div className="flex items-center justify-center gap-2" data-id="element-483">
              <EditIcon className="w-5 h-5" data-id="element-484" />
              <span data-id="element-485">Update Consent</span>
            </div>
          </Button>}

        {canRevoke && <Button onClick={onRevoke} variant="outline" className="w-full" data-id="element-486">
            <div className="flex items-center justify-center gap-2" data-id="element-487">
              <XCircleIcon className="w-5 h-5" data-id="element-488" />
              <span data-id="element-489">Revoke Consent</span>
            </div>
          </Button>}
      </div>

      {/* Withdrawal Rights */}
      <div className="p-4 rounded-xl" style={{
      backgroundColor: OPENFINANCE_THEME.colors.primary50,
      border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`
    }} data-id="element-490">
        <div className="text-xs font-semibold mb-2" style={{
        color: OPENFINANCE_THEME.colors.primary700
      }} data-id="element-491">
          Your Rights
        </div>
        <div className="text-xs leading-relaxed" style={{
        color: OPENFINANCE_THEME.colors.primary700
      }} data-id="element-492">
          You can withdraw this consent at any time before it expires.
          Withdrawing consent will immediately stop{' '}
          {isTPP ? consent.bankName : consent.tppTradingName} from accessing
          your data.
        </div>
      </div>
    </div>;
}